import { FC } from "react"
import { ActionElement } from "./ActionElement"
import { Divider, Grid, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { 
  AccountBalance, 
  AddHomeWork, 
  BarChart, 
  DescriptionOutlined, 
  Download, 
  FindInPage, 
  Payment, 
  PermContactCalendar, 
  Power,
  ReceiptOutlined, 
  TungstenOutlined, 
  Visibility 
} from "@mui/icons-material";
import useRedirectActions from "@/hooks/useRedirectActions";
import { contraction_url } from '@/constants/i18n';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ActionGroupTitleImage } from "./ActionGroupTitleImage";
import useContractation from "@/components/Contractation/hooks/useContractation";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import useAuth from "@/hooks/useAuth";

const ActionGroups: FC = () => {
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { 
    beginContractAction, beginInvoiceAction
  } = useRedirectActions({});

  const theme = useTheme();
  const breakpointDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const { activeContractations } = useContractation();
  const { tokenVals } = useAuth();

  return <Grid 
    container
    direction="row"
    spacing={{xs: 2, sm: 4, md: 6}}
    sx={{
      pl: {md: "10vw", lg: "15vw"}, 
      pr: {md: "10vw", lg: "15vw"}
    }}
  >
    {/* I use `gap` instead of `spacing` because it breaks when using 
      breakpoints. Perhaps an issue should be raised to the MUI team. 
      https://github.com/mui/material-ui/issues/32999
    */}
    <Grid container item direction="column" gap={{xs: 1}} sm={12} md={4}>

      <Grid item>
        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={2}
          alignItems="center"
        >
          <ActionGroupTitleImage 
            component="img"
            sx={{
              height: {
                xs: "40px",
                sm: "60px",
                md: "80px"
              },
              width: "auto"
            }}
            src={'/images/tabGroup/contratos.png'}
            fallbackComponent={
              <DescriptionOutlined 
                color="primary"
                sx={{
                  height: {
                    xs: "40px",
                    sm: "60px",
                    md: "80px"
                  },
                  width: "auto"
                }}
              />
            }
          />
          <Typography variant="h4">
            {t("common:text.contracts_title")}
          </Typography>
        </Stack>
      </Grid>

      {activeContractations.num > 0 && <Grid item>
        <ActionElement
          title={t("common:text.home_contract_create_title")}
          description={t("common:text.home_contract_create_desc")}
          icon={<AddHomeWork />}
          onClick={() => navigate(t(contraction_url))}
        />
      </Grid>
      }

      <Grid item>
        <ActionElement 
          title={t("common:text.home_contract_list_title")}
          description={t("common:text.home_contract_list_desc")}
          icon={<FindInPage />}
          onClick={() => beginContractAction("view", false)}
        />
      </Grid>

      <Grid item>
        <ActionElement 
          title={t("common:text.home_contract_download_title")}
          description={t("common:text.home_contract_download_desc")}
          icon={<Download />}
          onClick={() => beginContractAction("download")}
        />
      </Grid>

      {Settings.features.powerChange && (
        <Grid item>
          <ActionElement 
            toolTipProps={{
              title: tokenVals?.readonly ?
                t("common:text.token_readonly_tooltip")
              :
                "",
              placement: breakpointDownMd ? "bottom" : "right"
            }}
            title={t("common:text.home_contract_power_change_title")}
            description={t("common:text.home_contract_power_change_desc")}
            icon={<Power />}
            disabled={tokenVals?.readonly}
            showTooltip={tokenVals?.readonly}
            onClick={() => beginContractAction("powChange")}
          />
        </Grid>
      )}

      {Settings?.contract?.ibanChange?.enabled && (
        <Grid item>
          <ActionElement 
            toolTipProps={{
              title: tokenVals?.readonly ?
                t("common:text.token_readonly_tooltip")
              :
                "",
              placement: breakpointDownMd ? "bottom" : "right"
            }}
            title={t("common:text.home_contract_bank_change_title")}
            description={t("common:text.home_contract_bank_change_desc")}
            icon={<AccountBalance />}
            disabled={tokenVals?.readonly}
            showTooltip={tokenVals?.readonly}
            onClick={() => beginContractAction("ibanChange")}
          />
        </Grid>
      )}

      {Settings.features.notificacionAddressChange && (
        <Grid item>
          <ActionElement 
            toolTipProps={{
              title: tokenVals?.readonly ?
                t("common:text.token_readonly_tooltip")
              :
                "",
              placement: breakpointDownMd ? "bottom" : "right"
            }}
            title={t("common:text.home_contract_contact_change_title")}
            description={t("common:text.home_contract_contact_desc")}
            icon={<PermContactCalendar />}
            disabled={tokenVals?.readonly}
            showTooltip={tokenVals?.readonly}
            onClick={() => beginContractAction("notifAddrChange")}
          />
        </Grid>
      )}

      {Settings.features.noCesionSips && (
        <Grid item>
          <ActionElement
            toolTipProps={{
              title: tokenVals?.readonly ?
                t("common:text.token_readonly_tooltip")
              :
                "",
              placement: breakpointDownMd ? "bottom" : "right"
            }}
            title={t("common:text.home_contract_sips_change_title")}
            description={t("common:text.home_contract_sips_change_desc")}
            icon={<Visibility />}
            disabled={tokenVals?.readonly}
            showTooltip={tokenVals?.readonly}
            onClick={() => beginContractAction("noCesionSips")}
          />
        </Grid>
      )}

      <Grid item>
        <Divider variant="middle" sx={{display: {md: "none"}}}/>
      </Grid>

    </Grid>

    <Grid container item direction="column" gap={{xs: 1}} sm={12} md={4}>

      <Grid item>
        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={2}
          alignItems="center"
        >
          <ActionGroupTitleImage 
            component="img"
            sx={{
              height: {
                xs: "40px",
                sm: "60px",
                md: "80px"
              },
              width: "auto"
            }}
            src={'/images/tabGroup/facturas.png'}
            fallbackComponent={
              <ReceiptOutlined
                color="primary"
                sx={{
                  height: {
                    xs: "40px",
                    sm: "60px",
                    md: "80px"
                  },
                  width: "auto"
                }}
              />
            }
          />
          <Typography variant="h4">
            {t("common:text.home_invoices_title")}
          </Typography>
        </Stack>
      </Grid>

      <Grid item>
        <ActionElement
          title={t("common:text.home_invoices_list_title")}
          description={t("common:text.home_invoices_list_desc")}
          icon={<FindInPage />}
          onClick={() => beginInvoiceAction("view")}
        />
      </Grid>

      <Grid item>
        <ActionElement 
          title={t("common:text.home_invoices_download_title")}
          description={t("common:text.home_invoices_download_desc")}
          icon={<Download />}
          onClick={() => beginInvoiceAction("download")}
        />
      </Grid>

      <Grid item>
        <ActionElement 
          title={t("common:text.home_invoices_online_pay_title")}
          description={t("common:text.home_invoices_online_pay_desc")}
          icon={<Payment />}
          onClick={() => beginInvoiceAction("payOnline")}
        />
      </Grid>

      <Grid item>
        <Divider variant="middle" sx={{display: {md: "none"}}}/>
      </Grid>

    </Grid>

    <Grid container item direction="column" gap={{xs: 1}} sm={12} md={4}>

      <Grid item>
        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={2}
          alignItems="center"
        >
          <ActionGroupTitleImage 
            component="img"
            sx={{
              height: {
                xs: "40px",
                sm: "60px",
                md: "80px"
              },
              width: "auto"
            }}
            src={'/images/tabGroup/consumo.png'}
            fallbackComponent={
              <TungstenOutlined
                color="primary"
                sx={{
                  height: {
                    xs: "40px",
                    sm: "60px",
                    md: "80px"
                  },
                  width: "auto"
                }}
              />
            }
          />
          <Typography variant="h4">
            {t("common:text.home_consumption_title")}
          </Typography>
        </Stack>
      </Grid>

      <Grid item>
        <ActionElement
          title={t("common:text.home_consumption_view_title")}
          description={t("common:text.home_consumption_view_desc")}
          icon={<BarChart />}
          onClick={() => beginContractAction("consumption")}
        />
      </Grid>
      
    </Grid>

  </Grid>
}

export default ActionGroups;